// src/pages/sessions/components/LiveSession/hooks/useSubscriptions.js
import { useCallback, useRef, useEffect, useState } from 'react';
import { Track } from 'livekit-client';

const DEBUG = true;

// Helper function for Set comparison
function setsEqual(a, b) {
  if (a.size !== b.size) return false;
  for (const item of a) {
    if (!b.has(item)) return false;
  }
  return true;
}

// Helper function to compare arrays (for teacher state comparison)
function arraysEqual(a, b) {
  if (!a && !b) return true;
  if (!a || !b) return false;
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((val, idx) => val === sortedB[idx]);
}

export const useSubscriptions = ({
  roomRef,
  isTeacherUser,
  savedVirtualRoomIdRef,
  teacherBroadcastRooms,
  teacherMonitorRooms,
  teacherMonitorSilentRooms, // NEW: Set of room IDs being monitored silently
  participantVirtualRooms,
  onParticipantVisibilityChanged,
  remoteTeacherStates,
  setRemoteTeacherStates,
}) => {
  const lastAppliedStateRef = useRef(null);
  const subscriptionStateRef = useRef(new Map());
  const participantReadyRef = useRef(new Set());
  const visibleParticipantsRef = useRef(new Set());
  const metadataDebounceRef = useRef({});
  
  // Grace period timers: when a participant's tracks drop to 0, delay removal
  // from visibility by 0.5s. If they reconnect within that window (same identity),
  // cancel the timer — tile never unmounts, no flicker.
  const visibilityRemovalTimersRef = useRef({});
  
  // Reactive state: which participants are currently in grace period (disconnected
  // but still held visible). VideoGrid uses this to show a "Disconnected" badge.
  // Map: identity → { displayName, metadata }
  const [gracePeriodParticipants, setGracePeriodParticipants] = useState(new Map());
  
  // CRITICAL FIX: Queue with state snapshots instead of simple lock
  const subscriptionQueue = useRef({
    isLocked: false,
    pendingUpdates: [] // Array of state snapshots
  });
  
  // Track the actual subscription state to prevent flip-flopping
  const actualSubscriptionStateRef = useRef(new Map());
  
  // Track student room changes
  const lastStudentRoomRef = useRef(null);
  
  // Debounce timeout ref
  const applyTimeoutRef = useRef(null);

  // Helper function to normalize room IDs
  const normalizeRoomId = (roomId) => {
    if (roomId === null || roomId === undefined || roomId === 0 || roomId === "0") {
      return null;
    }
    return roomId;
  };

  // Check if participant is ready for subscription
  const isParticipantReady = (participant) => {
    // If already marked as ready (including fallback after metadata timeout), they're ready
    if (participantReadyRef.current.has(participant.identity)) {
      return true;
    }
    
    // Otherwise, require metadata
    if (!participant.metadata) {
      console.log(`[Subscriptions] Participant ${participant.identity} not ready - no metadata yet`);
      return false;
    }
    
    // Has metadata, mark as ready
    participantReadyRef.current.add(participant.identity);
    console.log(`[Subscriptions] Participant ${participant.identity} marked as ready (has metadata)`);
    return true;
  };

  const applyAllCore = useCallback(async (forcedState = null) => {
    // Check if locked
    if (subscriptionQueue.current.isLocked) {
      // Create state snapshot
      const stateSnapshot = {
        isTeacher: isTeacherUser,
        myRoom: normalizeRoomId(savedVirtualRoomIdRef?.current),
        broadcasting: teacherBroadcastRooms ? new Set(teacherBroadcastRooms) : new Set(),
        monitoring: teacherMonitorRooms ? new Set(teacherMonitorRooms) : new Set(),
        monitoringSilent: teacherMonitorSilentRooms ? new Set(teacherMonitorSilentRooms) : new Set(), // NEW
        remoteTeachers: JSON.parse(JSON.stringify(remoteTeacherStates || {})), // Deep clone
        timestamp: Date.now()
      };
      
      // Add to queue if it's meaningfully different from the last queued update
      const queue = subscriptionQueue.current.pendingUpdates;
      const lastQueued = queue[queue.length - 1];
      
      if (!lastQueued || 
          stateSnapshot.myRoom !== lastQueued.myRoom ||
          !setsEqual(stateSnapshot.broadcasting, lastQueued.broadcasting) ||
          !setsEqual(stateSnapshot.monitoring, lastQueued.monitoring) ||
          !setsEqual(stateSnapshot.monitoringSilent, lastQueued.monitoringSilent) || // NEW
          JSON.stringify(stateSnapshot.remoteTeachers) !== JSON.stringify(lastQueued.remoteTeachers)) {
        
        console.log('[Subscriptions] Queueing update', {
          queueLength: queue.length + 1,
          myRoom: stateSnapshot.myRoom,
          broadcasting: Array.from(stateSnapshot.broadcasting),
          monitoring: Array.from(stateSnapshot.monitoring),
          monitoringSilent: Array.from(stateSnapshot.monitoringSilent) // NEW
        });
        
        queue.push(stateSnapshot);
        
        // Limit queue size to prevent memory issues
        if (queue.length > 5) {
          queue.splice(0, queue.length - 5);
        }
      }
      
      return;
    }

    if (!roomRef.current) {
      console.log('[Subscriptions] No room yet');
      return;
    }

    // Lock the queue
    subscriptionQueue.current.isLocked = true;

    try {
      const room = roomRef.current;
      const participants = Array.from(room.remoteParticipants.values());
      
      if (participants.length === 0) {
        console.log('[Subscriptions] No remote participants');
        // DON'T clear visibility if grace period timers are active — participants
        // may be in the middle of a reconnect (browser refresh). The timers will
        // handle cleanup if they don't come back within 3 seconds.
        const hasActiveGraceTimers = Object.keys(visibilityRemovalTimersRef.current).length > 0;
        if (hasActiveGraceTimers) {
          console.log('[Subscriptions] Grace period timers active — keeping visibility intact');
        } else if (visibleParticipantsRef.current.size > 0) {
          visibleParticipantsRef.current.clear();
          if (onParticipantVisibilityChanged) {
            onParticipantVisibilityChanged(new Set());
          }
        }
        return;
      }

      const readyParticipants = participants.filter(p => isParticipantReady(p));
      if (readyParticipants.length === 0) {
        console.log('[Subscriptions] No participants ready yet');
        return;
      }

      // Use forced state if provided, otherwise use current state
      const stateToApply = forcedState || {
        isTeacher: isTeacherUser,
        myRoom: normalizeRoomId(savedVirtualRoomIdRef?.current),
        broadcasting: teacherBroadcastRooms ? new Set(teacherBroadcastRooms) : new Set(),
        monitoring: teacherMonitorRooms ? new Set(teacherMonitorRooms) : new Set(),
        monitoringSilent: teacherMonitorSilentRooms ? new Set(teacherMonitorSilentRooms) : new Set(), // NEW
        remoteTeachers: remoteTeacherStates || {}
      };

      // CRITICAL FIX: Include track publication info in fingerprint
      const participantStates = readyParticipants.map(p => {
        const tracks = Array.from(p.trackPublications.values()).map(pub => ({
          sid: pub.trackSid,
          kind: pub.kind,
          source: pub.source
        }));
        return {
          id: p.identity,
          metadata: p.metadata,
          tracks: tracks // ADDED: Include tracks in state comparison
        };
      });
      
      const currentFingerprint = JSON.stringify({
        isTeacher: stateToApply.isTeacher,
        myRoom: stateToApply.myRoom,
        broadcasting: Array.from(stateToApply.broadcasting).sort(),
        monitoring: Array.from(stateToApply.monitoring).sort(),
        monitoringSilent: Array.from(stateToApply.monitoringSilent).sort(), // NEW
        participantStates: participantStates,
        remoteTeachers: stateToApply.remoteTeachers
      });

      // Skip if identical to last applied
      if (lastAppliedStateRef.current === currentFingerprint) {
        console.log('[Subscriptions] State unchanged, skipping');
        return;
      }

      lastAppliedStateRef.current = currentFingerprint;

      console.log('[Subscriptions] Applying with:', {
        isTeacher: stateToApply.isTeacher,
        myRoom: stateToApply.myRoom,
        broadcasting: Array.from(stateToApply.broadcasting),
        monitoring: Array.from(stateToApply.monitoring),
        monitoringSilent: Array.from(stateToApply.monitoringSilent), // NEW
        participantCount: readyParticipants.length,
        remoteTeacherCount: Object.keys(stateToApply.remoteTeachers).length
      });

      const newVisibleParticipants = new Set();
      const desiredSubscriptions = new Map();

      // First pass: determine what SHOULD be subscribed
      for (const participant of readyParticipants) {
        const identity = participant.identity;
        
        let metadata = {};
        try {
          metadata = JSON.parse(participant.metadata || '{}');
        } catch (e) {
          console.warn('[Subscriptions] Metadata parse error for', identity, '- treating as no metadata:', e.message);
          // Continue with empty metadata instead of skipping participant
          metadata = {};
        }

        const isRemoteTeacher = metadata.role === 'teacher';
        
        // FIX: Use WebSocket state (participantVirtualRooms) as primary source of truth,
        // fall back to metadata only for new participants we don't have state for yet
        const remoteRoomId = normalizeRoomId(participantVirtualRooms?.[identity] !== undefined 
          ? participantVirtualRooms[identity] 
          : metadata.virtualRoomId);

        let shouldSubscribeToParticipant = false;

        if (stateToApply.isTeacher) {
          // TEACHER LOGIC
          if (isRemoteTeacher) {
            shouldSubscribeToParticipant = false;
          } else {
            if (stateToApply.myRoom === null) {
              if (remoteRoomId === null) {
                shouldSubscribeToParticipant = true;
              } else {
                const roomNum = Number(remoteRoomId);
                // CRITICAL FIX: Include monitoringSilent in subscription check
                shouldSubscribeToParticipant = stateToApply.broadcasting.has(roomNum) || 
                                              stateToApply.monitoring.has(roomNum) ||
                                              stateToApply.monitoringSilent.has(roomNum);
              }
            } else {
              shouldSubscribeToParticipant = Number(stateToApply.myRoom) === Number(remoteRoomId);
            }
          }
        } else {
          // STUDENT LOGIC
          if (isRemoteTeacher) {
            // Student viewing teacher
            // Teacher ALWAYS broadcasts to main room, and to breakout rooms they've joined
            const teacherState = stateToApply.remoteTeachers[identity] || {};
            const teacherBroadcasting = teacherState.broadcastingTo || [];
            
            if (stateToApply.myRoom === null) {
              // Student in main room: teacher ALWAYS broadcasts to main
              shouldSubscribeToParticipant = true;
            } else {
              const roomNum = Number(stateToApply.myRoom);
              shouldSubscribeToParticipant = teacherBroadcasting.some(room => Number(room) === roomNum);
            }
          } else {
            // Student-to-student
            if (stateToApply.myRoom === null && remoteRoomId === null) {
              shouldSubscribeToParticipant = true;
            } else if (stateToApply.myRoom !== null && remoteRoomId !== null) {
              shouldSubscribeToParticipant = Number(stateToApply.myRoom) === Number(remoteRoomId);
            }
          }
        }

        if (shouldSubscribeToParticipant) {
          newVisibleParticipants.add(identity);
        }

        // ============================================================================
        // CRITICAL CHANGE: Per-track subscription logic for silent monitoring
        // ============================================================================
        // Check if this participant's room is being monitored silently
        const roomNum = remoteRoomId !== null ? Number(remoteRoomId) : null;
        const isMonitoringSilent = roomNum !== null && 
                                  stateToApply.isTeacher && 
                                  stateToApply.monitoringSilent.has(roomNum);
        
        // Store desired state for EACH track individually
        for (const publication of participant.trackPublications.values()) {
          if (!publication || !publication.trackSid) continue;
          if (!stateToApply.isTeacher && publication.trackName === 'whiteboard') continue;
          
          const trackKey = `${identity}-${publication.trackSid}`;
          let trackShouldSubscribe = shouldSubscribeToParticipant;
          
          // If monitoring silently, skip audio tracks but keep video
          if (isMonitoringSilent && shouldSubscribeToParticipant) {
            const isAudioTrack = publication.kind === Track.Kind.Audio || 
                                publication.source === Track.Source.Microphone;
            
            if (isAudioTrack) {
              trackShouldSubscribe = false; // Skip audio when monitoring silently
              console.log(`[Subscriptions] Silent monitor - skipping audio for ${identity} in room ${roomNum}`);
            }
          }
          
          desiredSubscriptions.set(trackKey, {
            shouldSubscribe: trackShouldSubscribe,
            publication,
            identity
          });
        }
      }

      // Second pass: apply ONLY the changes needed
      let changesApplied = 0;
      const changes = [];

      for (const [trackKey, desired] of desiredSubscriptions) {
        const { shouldSubscribe, publication, identity } = desired;
        
        if (!publication || typeof publication.setSubscribed !== 'function') {
          console.error(`[Subscriptions] Invalid publication for ${identity}`);
          continue;
        }
        
        // SAFETY FIX: Check LiveKit's actual state, not just our tracking
        const trackedState = actualSubscriptionStateRef.current.get(trackKey);
        const livekitState = publication.isSubscribed;
        
        // If our tracking disagrees with LiveKit reality, correct it
        if (trackedState !== undefined && trackedState !== livekitState) {
          console.warn(`[Subscriptions] State mismatch for ${identity} (${publication.kind}): tracked=${trackedState}, actual=${livekitState}`);
          actualSubscriptionStateRef.current.set(trackKey, livekitState);
        }
        
        // Compare LiveKit's actual state against desired state
        if (livekitState !== shouldSubscribe) {
          changes.push({ trackKey, shouldSubscribe, publication, identity });
        }
      }

      // Batch apply
      for (const change of changes) {
        const { trackKey, shouldSubscribe, publication, identity } = change;
        
        try {
          console.log(`[Subscriptions] ${shouldSubscribe ? 'SUB' : 'UNSUB'} ${identity}'s ${publication.kind}`);
          actualSubscriptionStateRef.current.set(trackKey, shouldSubscribe);
          
          const result = publication.setSubscribed(shouldSubscribe);
          if (result && typeof result.then === 'function') {
            await result;
          }
          
          changesApplied++;
        } catch (error) {
          console.error(`[Subscriptions] Error on ${identity}:`, error);
          actualSubscriptionStateRef.current.delete(trackKey);
        }
      }

      console.log(`[Subscriptions] Applied ${changesApplied} changes`);
      
      // Update visibility
      const visibilityChanged = 
        newVisibleParticipants.size !== visibleParticipantsRef.current.size ||
        [...newVisibleParticipants].some(id => !visibleParticipantsRef.current.has(id)) ||
        [...visibleParticipantsRef.current].some(id => !newVisibleParticipants.has(id));
      
      if (visibilityChanged || changesApplied > 0) {
        console.log(`[Subscriptions] Visibility updated - ${visibleParticipantsRef.current.size} -> ${newVisibleParticipants.size}`);
        visibleParticipantsRef.current = newVisibleParticipants;
        if (onParticipantVisibilityChanged) {
          onParticipantVisibilityChanged(newVisibleParticipants);
        }
      }
      
    } catch (error) {
      console.error('[Subscriptions] Error in applyAllCore:', error);
    } finally {
      subscriptionQueue.current.isLocked = false;
      
      // Process next queued update if any
      const nextUpdate = subscriptionQueue.current.pendingUpdates.shift();
      if (nextUpdate) {
        console.log('[Subscriptions] Processing queued update');
        setTimeout(() => applyAllCore(nextUpdate), 0);
      }
    }
  }, [
    isTeacherUser,
    teacherBroadcastRooms,
    teacherMonitorRooms,
    teacherMonitorSilentRooms,
    savedVirtualRoomIdRef,
    participantVirtualRooms,
    remoteTeacherStates,
    roomRef,
    onParticipantVisibilityChanged
  ]);

  const applyAll = useCallback(() => {
    if (applyTimeoutRef.current) {
      clearTimeout(applyTimeoutRef.current);
    }
    
    applyTimeoutRef.current = setTimeout(() => {
      applyTimeoutRef.current = null;
      applyAllCore();
    }, 50);
  }, [applyAllCore]);

  useEffect(() => {
    const room = roomRef.current;
    if (!room) {
      console.log('[Subscriptions] No room ref for event listeners');
      return;
    }

    const handleParticipantConnected = (participant) => {
      console.log(`[Subscriptions] Participant connected: ${participant.identity}`);
      
      // NOTE: We intentionally do NOT cancel the visibility removal grace timer here.
      // During pre-join/permission flows, the student's browser connects to LiveKit,
      // publishes tracks, then disconnects — repeatedly. Cancelling the timer each time
      // creates phantom tiles that never disappear. Instead, let the timer run to
      // completion — its callback checks if the participant has subscribed tracks,
      // so legitimate reconnects stay visible and phantoms get cleaned up.
      
      if (!participant.metadata) {
        console.log(`[Subscriptions] Waiting for metadata: ${participant.identity}`);
        
        const checkMetadata = setInterval(() => {
          if (participant.metadata) {
            console.log(`[Subscriptions] Metadata arrived for: ${participant.identity}`);
            clearInterval(checkMetadata);
            participantReadyRef.current.add(participant.identity);
            applyAll();
          }
        }, 100);
        
        // After 5 seconds, mark participant ready even without metadata
        // This ensures they can still be subscribed to (teacher in main room subscribes to all)
        setTimeout(() => {
          clearInterval(checkMetadata);
          if (!participantReadyRef.current.has(participant.identity)) {
            console.log(`[Subscriptions] Metadata timeout for ${participant.identity} - marking ready anyway`);
            participantReadyRef.current.add(participant.identity);
            lastAppliedStateRef.current = null; // Force re-evaluation
            applyAll();
          }
        }, 5000);
      } else {
        participantReadyRef.current.add(participant.identity);
        applyAll();
      }
    };

    const handleTrackPublished = (publication, participant) => {
      console.log(`[Subscriptions] Track published: ${participant.identity} - ${publication.kind}`);
      
      // CRITICAL: For screen shares, subscribe immediately (except whiteboard for non-teachers)
      if (publication.source === Track.Source.ScreenShare) {
        // Whiteboard egress track is for recording only — students see the interactive
        // WhiteboardCanvas via data channel sync. Don't subscribe non-teachers.
        if (!isTeacherUser && publication.trackName === 'whiteboard') {
          console.log(`[Subscriptions] Skipping whiteboard screen share for non-teacher`);
          return;
        }
        
        console.log(`[Subscriptions] Screen share published, subscribing immediately`);
        const trackKey = `${participant.identity}-${publication.trackSid}`;
        
        actualSubscriptionStateRef.current.set(trackKey, true);
        
        (async () => {
          try {
            await publication.setSubscribed(true);
            console.log(`[Subscriptions] Screen share subscription successful`);
          } catch (err) {
            console.error(`[Subscriptions] Screen share subscription failed:`, err);
            actualSubscriptionStateRef.current.delete(trackKey);
          }
          
          // Still apply all to handle other tracks
          applyAll();
        })();
        return;
      }
      
      // CRITICAL FIX: For microphone tracks without metadata, subscribe immediately
      // if we're a teacher in the main room. This prevents race conditions where
      // metadata arrives late due to network issues (e.g., VPN adding latency).
      if (publication.source === Track.Source.Microphone && !participant.metadata) {
        const myRoom = normalizeRoomId(savedVirtualRoomIdRef?.current);
        
        // Teacher in main room should hear all students by default
        if (isTeacherUser && myRoom === null) {
          console.log(`[Subscriptions] Audio track published without metadata for ${participant.identity} - subscribing immediately (teacher in main room)`);
          const trackKey = `${participant.identity}-${publication.trackSid}`;
          
          actualSubscriptionStateRef.current.set(trackKey, true);
          
          (async () => {
            try {
              await publication.setSubscribed(true);
              console.log(`[Subscriptions] Immediate audio subscription successful for ${participant.identity}`);
            } catch (err) {
              console.error(`[Subscriptions] Immediate audio subscription failed:`, err);
              actualSubscriptionStateRef.current.delete(trackKey);
            }
            
            // Schedule retry to handle metadata-based logic later (e.g., if student is actually in breakout)
            setTimeout(() => {
              lastAppliedStateRef.current = null;
              applyAll();
            }, 500);
          })();
          return;
        }
      }
      
      // CRITICAL: Apply immediately for track publishes, don't debounce
      // Also clear fingerprint and schedule follow-up in case queue was locked
      lastAppliedStateRef.current = null;
      applyAllCore();
      
      // Schedule a follow-up to catch any tracks that got queued but not processed
      // This matches the pattern used for microphone tracks
      setTimeout(() => {
        lastAppliedStateRef.current = null;
        applyAll();
      }, 200);
    };

    const handleMetadataChanged = (prevMetadata, participant) => {
      console.log(`[Subscriptions] Metadata changed: ${participant.identity}`);
      participantReadyRef.current.add(participant.identity);
      
      if (!isTeacherUser) {
        let metadata = {};
        try {
          metadata = JSON.parse(participant.metadata || '{}');
        } catch (e) {
          console.error('[Subscriptions] Failed to parse metadata:', e);
        }
        
        if (metadata.role === 'teacher' && setRemoteTeacherStates) {
          const newMetadataState = {
            broadcastingTo: metadata.broadcastingTo || [],
            monitoringRooms: metadata.monitoringRooms || [],
            timestamp: Date.now(),
            source: 'metadata'
          };
          
          setRemoteTeacherStates(prev => {
            const currentState = prev[participant.identity];
            
            // If no current state, use metadata
            if (!currentState) {
              console.log(`[Metadata] Using metadata for new teacher ${participant.identity}`);
              return {
                ...prev,
                [participant.identity]: newMetadataState
              };
            }
            
            // FIX: Check if metadata is DIFFERENT from current state (not just "has more data")
            // This ensures we update when teacher STOPS broadcasting (empty arrays)
            const broadcastingChanged = !arraysEqual(
              currentState.broadcastingTo, 
              newMetadataState.broadcastingTo
            );
            const monitoringChanged = !arraysEqual(
              currentState.monitoringRooms, 
              newMetadataState.monitoringRooms
            );
            
            if (broadcastingChanged || monitoringChanged) {
              console.log(`[Metadata] Updating teacher state for ${participant.identity} (state changed)`, {
                oldBroadcasting: currentState.broadcastingTo,
                newBroadcasting: newMetadataState.broadcastingTo,
                oldMonitoring: currentState.monitoringRooms,
                newMonitoring: newMetadataState.monitoringRooms
              });
              return {
                ...prev,
                [participant.identity]: newMetadataState
              };
            }
            
            console.log(`[Metadata] Keeping existing state for ${participant.identity} (no change)`);
            return prev;
          });
          
          lastAppliedStateRef.current = null;
          applyAll();
          return;
        }
      }
      
      if (metadataDebounceRef.current[participant.identity]) {
        clearTimeout(metadataDebounceRef.current[participant.identity]);
      }
      
      metadataDebounceRef.current[participant.identity] = setTimeout(() => {
        delete metadataDebounceRef.current[participant.identity];
        
        lastAppliedStateRef.current = null;
        
        for (const [key] of actualSubscriptionStateRef.current) {
          if (key.startsWith(`${participant.identity}-`)) {
            actualSubscriptionStateRef.current.delete(key);
          }
        }
        
        applyAll();
      }, 50);
    };

    const handleTrackSubscribed = (track, publication, participant) => {
      console.log(`[Subscriptions] Track subscribed: ${participant.identity} - ${track.kind}`);
      const trackKey = `${participant.identity}-${publication.trackSid}`;
      actualSubscriptionStateRef.current.set(trackKey, true);
      
      // Clear the "Disconnected" badge immediately — they're back
      const identity = participant.identity;
      setGracePeriodParticipants(prev => {
        if (!prev.has(identity)) return prev;
        const next = new Map(prev);
        next.delete(identity);
        return next;
      });
      
      // NOTE: We intentionally do NOT cancel the visibility removal grace timer.
      // Let the timer run — its callback checks for subscribed tracks, so if this
      // participant has tracks when it fires, they'll stay visible.
    };

    const handleTrackUnsubscribed = (track, publication, participant) => {
      console.log(`[Subscriptions] Track unsubscribed: ${participant.identity} - ${track.kind}`);
      const trackKey = `${participant.identity}-${publication.trackSid}`;
      actualSubscriptionStateRef.current.set(trackKey, false);
      
      // Screen share / screen audio stopping is a deliberate action, not a disconnect signal.
      // Skip grace-period logic entirely — it would incorrectly show "Verbinding verbroken".
      if (
        publication.source === Track.Source.ScreenShare ||
        publication.source === Track.Source.ScreenShareAudio
      ) {
        return;
      }
      
      let subscribedTracks = 0;
      for (const [key, subscribed] of actualSubscriptionStateRef.current) {
        if (key.startsWith(`${participant.identity}-`) && subscribed) {
          subscribedTracks++;
        }
      }
      
      if (subscribedTracks === 0 && visibleParticipantsRef.current.has(participant.identity)) {
        const identity = participant.identity;
        
        // Cache participant info for the disconnected tile
        let displayName = '';
        try {
          const meta = JSON.parse(participant.metadata || '{}');
          displayName = meta.displayName || participant.name || '';
        } catch (e) {}
        
        // Cancel any existing timer for this identity
        if (visibilityRemovalTimersRef.current[identity]) {
          clearTimeout(visibilityRemovalTimersRef.current[identity]);
        }
        
        // Add to grace period state — VideoGrid shows "Disconnected" badge
        setGracePeriodParticipants(prev => {
          const next = new Map(prev);
          next.set(identity, { 
            displayName, 
            metadata: participant.metadata || '{}',
            identity 
          });
          return next;
        });
        
        console.log(`[Subscriptions] Scheduling deferred visibility removal for ${identity} (0.5s grace period)`);
        visibilityRemovalTimersRef.current[identity] = setTimeout(() => {
          delete visibilityRemovalTimersRef.current[identity];
          
          if (visibleParticipantsRef.current.has(identity)) {
            const room = roomRef.current;
            let isConnected = false;
            if (room) {
              for (const [, p] of room.remoteParticipants) {
                if (p.identity === identity) {
                  isConnected = true;
                  break;
                }
              }
            }
            
            let currentSubscribed = 0;
            for (const [key, subscribed] of actualSubscriptionStateRef.current) {
              if (key.startsWith(`${identity}-`) && subscribed) {
                currentSubscribed++;
              }
            }
            
            if (isConnected && currentSubscribed > 0) {
              console.log(`[Subscriptions] Grace period expired but ${identity} is connected with ${currentSubscribed} tracks — keeping visible`);
            } else {
              console.log(`[Subscriptions] Grace period expired, removing ${identity} from visibility (connected=${isConnected}, tracks=${currentSubscribed})`);
              const newVisible = new Set(visibleParticipantsRef.current);
              newVisible.delete(identity);
              visibleParticipantsRef.current = newVisible;
              
              if (onParticipantVisibilityChanged) {
                onParticipantVisibilityChanged(newVisible);
              }
            }
          }
          
          // Always clear from grace period state when timer completes
          setGracePeriodParticipants(prev => {
            const next = new Map(prev);
            next.delete(identity);
            return next;
          });
        }, 500);
      }
      
      lastAppliedStateRef.current = null;
      applyAll();
    };

    const handleTrackUnpublished = (publication, participant) => {
      console.log(`[Subscriptions] Track unpublished: ${participant.identity}`);
      const trackKey = `${participant.identity}-${publication.trackSid}`;
      actualSubscriptionStateRef.current.delete(trackKey);
      
      // CRITICAL FIX: Clear state when track unpublished
      lastAppliedStateRef.current = null;
    };

    const handleParticipantDisconnected = (participant) => {
      console.log(`[Subscriptions] Participant disconnected: ${participant.identity}`);
      
      // Clean up stale subscription state entries for the old connection's trackSids.
      // This is safe — old trackSids won't match new connection's trackSids.
      for (const [key] of actualSubscriptionStateRef.current) {
        if (key.startsWith(`${participant.identity}-`)) {
          actualSubscriptionStateRef.current.delete(key);
        }
      }
      
      if (metadataDebounceRef.current[participant.identity]) {
        clearTimeout(metadataDebounceRef.current[participant.identity]);
        delete metadataDebounceRef.current[participant.identity];
      }
      
      // DON'T remove from visibility immediately. A grace period timer should already
      // be running from handleTrackUnsubscribed. If not (edge case), start one now.
      // The timer in handleParticipantConnected will cancel it if they reconnect.
      if (visibleParticipantsRef.current.has(participant.identity)) {
        const identity = participant.identity;
        
        // Show "Disconnected" badge immediately if not already in grace period
        setGracePeriodParticipants(prev => {
          if (prev.has(identity)) return prev;
          let displayName = '';
          try {
            const meta = JSON.parse(participant.metadata || '{}');
            displayName = meta.displayName || participant.name || '';
          } catch (e) {}
          const next = new Map(prev);
          next.set(identity, { displayName, metadata: participant.metadata || '{}', identity });
          return next;
        });
        
        if (!visibilityRemovalTimersRef.current[identity]) {
          console.log(`[Subscriptions] Scheduling deferred visibility removal for ${identity} on disconnect (0.5s grace)`);
          visibilityRemovalTimersRef.current[identity] = setTimeout(() => {
            delete visibilityRemovalTimersRef.current[identity];
            if (visibleParticipantsRef.current.has(identity)) {
              const room = roomRef.current;
              let isConnected = false;
              if (room) {
                for (const [, p] of room.remoteParticipants) {
                  if (p.identity === identity) {
                    isConnected = true;
                    break;
                  }
                }
              }
              
              let currentSubscribed = 0;
              for (const [key, subscribed] of actualSubscriptionStateRef.current) {
                if (key.startsWith(`${identity}-`) && subscribed) {
                  currentSubscribed++;
                }
              }
              
              if (isConnected && currentSubscribed > 0) {
                console.log(`[Subscriptions] Disconnect grace expired but ${identity} is connected with ${currentSubscribed} tracks — keeping visible`);
              } else {
                console.log(`[Subscriptions] Disconnect grace expired, removing ${identity} from visibility (connected=${isConnected}, tracks=${currentSubscribed})`);
                const newVisible = new Set(visibleParticipantsRef.current);
                newVisible.delete(identity);
                visibleParticipantsRef.current = newVisible;
                
                if (onParticipantVisibilityChanged) {
                  onParticipantVisibilityChanged(newVisible);
                }
              }
            }
            
            // Clear grace period visual state
            setGracePeriodParticipants(prev => {
              const next = new Map(prev);
              next.delete(identity);
              return next;
            });
          }, 500);
        } else {
          console.log(`[Subscriptions] Grace period timer already running for ${participant.identity}`);
        }
      }
      
      // DON'T delete participantReadyRef here — if they reconnect within grace period
      // and arrive with metadata, handleParticipantConnected will re-add it.
      // But DO delete it after the grace period expires (handled implicitly — applyAll
      // calls isParticipantReady which re-adds on metadata check).
      participantReadyRef.current.delete(participant.identity);
    };

    room.on('participantConnected', handleParticipantConnected);
    room.on('participantDisconnected', handleParticipantDisconnected);
    room.on('trackPublished', handleTrackPublished);
    room.on('trackUnpublished', handleTrackUnpublished);
    room.on('participantMetadataChanged', handleMetadataChanged);
    room.on('trackSubscribed', handleTrackSubscribed);
    room.on('trackUnsubscribed', handleTrackUnsubscribed);

    // Initial application
    applyAll();

    return () => {
      room.off('participantConnected', handleParticipantConnected);
      room.off('participantDisconnected', handleParticipantDisconnected);
      room.off('trackPublished', handleTrackPublished);
      room.off('trackUnpublished', handleTrackUnpublished);
      room.off('participantMetadataChanged', handleMetadataChanged);
      room.off('trackSubscribed', handleTrackSubscribed);
      room.off('trackUnsubscribed', handleTrackUnsubscribed);
      
      for (const timeout of Object.values(metadataDebounceRef.current)) {
        clearTimeout(timeout);
      }
      metadataDebounceRef.current = {};
      
      // Clean up visibility grace period timers
      for (const timeout of Object.values(visibilityRemovalTimersRef.current)) {
        clearTimeout(timeout);
      }
      visibilityRemovalTimersRef.current = {};
      
      if (applyTimeoutRef.current) {
        clearTimeout(applyTimeoutRef.current);
        applyTimeoutRef.current = null;
      }
    };
  }, [roomRef, applyAll, applyAllCore, savedVirtualRoomIdRef, onParticipantVisibilityChanged, isTeacherUser, setRemoteTeacherStates]);

  return {
    applyAll,
    savedVirtualRoomIdRef,
    visibleParticipants: visibleParticipantsRef.current,
    gracePeriodParticipants
  };
};